import React, { useEffect, useState } from "react";
import { CaseStatus } from "@kahunmedical/common-wtypes";
import {
  DialogActions,
  styled,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { ClinicDialog } from "../elements/clinicDialog";
import { ClinicFormInput } from "../elements/clinicFormInput";
import { ClinicDateTimePicker } from "../elements/clinicDateTimePicker";
import { PatientFilterParams } from "./patientsTable";
import { ToggleButton, ToggleButtonGroup } from "@mui/lab";
//region [[ Styles ]]

const FilterRowView = styled("div")({
  display: "flex",
  alignItems: "end",
  gap: 10,
  paddingTop: 10,
});

const ActionsDivider = styled(Divider)({
  margin: "24px -40px 10px -40px",
});

const FilterCheckBoxLabel = styled(FormControlLabel)({
  "& span": { fontSize: 13, whiteSpace: "nowrap" },
});

const ToggleFinished = styled(ToggleButtonGroup)({
  "& button": {
    fontSize: 13,
    textTransform: "none",
  },
});

const ErrorText = styled("div")({
  color: "red",
  fontSize: 14,
  marginTop: 14,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientsTableFilterProps {
  filterParams: PatientFilterParams;
  defaultFilter: PatientFilterParams;
  onClose: () => void;
  onFilter: (filter: PatientFilterParams) => void;
  locale?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientsTableFilter = ({ ...props }: PatientsTableFilterProps) => {
  const [filterParams, setFilterParams] = useState<PatientFilterParams>(
    props.filterParams,
  );
  const [errMsg, setErrMsg] = useState<string>("");
  const resetFilter = () => {
    setFilterParams(props.defaultFilter);
  };

  const statusToggle = (updated: CaseStatus) => {
    setFilterParams((prevState) => ({
      ...prevState,
      statuses: prevState.statuses?.includes(updated)
        ? prevState.statuses?.filter((s) => s !== updated)
        : [...prevState.statuses, updated],
    }));
  };
  const hasStatus = (s: CaseStatus) => filterParams.statuses?.includes(s);

  const handleFilter = () => {
    if (!errMsg) {
      props.onFilter(filterParams);
      props.onClose();
    }
  };

  useEffect(() => {
    setErrMsg(
      filterParams.fromDate &&
        filterParams.toDate &&
        !filterParams.today &&
        filterParams.fromDate.setHours(0, 0, 0, 0) >
          filterParams.toDate.setHours(23, 59, 59, 999)
        ? "From date cannot be greater than To date"
        : "",
    );
  }, [filterParams.toDate, filterParams.fromDate, filterParams.today]);

  return (
    <ClinicDialog
      open={true}
      onClose={() => props.onClose()}
      closeButton={true}
    >
      <DialogContent>
        <strong>Date of visit</strong>
        <FilterRowView style={{ paddingBottom: 26 }}>
          <ClinicFormInput
            id="filterFromDate"
            label="From:"
            margin={0}
            disabled={filterParams.today}
            customInput={
              <ClinicDateTimePicker
                value={filterParams.fromDate}
                onChange={(dateVal) => {
                  setFilterParams((prevState) => ({
                    ...prevState,
                    fromDate: dateVal,
                  }));
                }}
                locale={props.locale}
                showTime={false}
                disabled={filterParams.today}
              />
            }
          />
          <ClinicFormInput
            id="filterToDate"
            label="To:"
            margin={0}
            disabled={filterParams.today}
            customInput={
              <ClinicDateTimePicker
                value={filterParams.toDate}
                onChange={(dateVal) => {
                  setFilterParams((prevState) => ({
                    ...prevState,
                    toDate: dateVal,
                  }));
                }}
                locale={props.locale}
                showTime={false}
                disabled={filterParams.today || !filterParams.fromDate}
              />
            }
          />
          <FilterCheckBoxLabel
            control={
              <Checkbox
                size={"small"}
                checked={filterParams.today}
                onChange={(e) =>
                  setFilterParams((prevState) => ({
                    ...prevState,
                    today: e.target.checked,
                  }))
                }
                name="checkedToday"
                color="primary"
              />
            }
            label="Today"
          />
        </FilterRowView>

        <strong>Assessment Status</strong>
        <FilterRowView>
          {/*<FilterCheckBoxLabel*/}
          {/*  control={*/}
          {/*    <Checkbox*/}
          {/*      size={"small"}*/}
          {/*      checked={hasStatus(CaseStatus.COMPLETED)}*/}
          {/*      onChange={() => statusToggle(CaseStatus.COMPLETED)}*/}
          {/*      name="statusCompleted"*/}
          {/*      color="primary"*/}
          {/*    />*/}
          {/*  }*/}
          {/*  label="Completed"*/}
          {/*/>*/}
          {/*<FilterCheckBoxLabel*/}
          {/*  control={*/}
          {/*    <Checkbox*/}
          {/*      size={"small"}*/}
          {/*      checked={hasStatus(CaseStatus.ABANDONED)}*/}
          {/*      onChange={() => statusToggle(CaseStatus.ABANDONED)}*/}
          {/*      name="checkedToday"*/}
          {/*      color="primary"*/}
          {/*    />*/}
          {/*  }*/}
          {/*  label="Partially Completed"*/}
          {/*/>*/}
          {/*<FilterCheckBoxLabel*/}
          {/*  control={*/}
          {/*    <Checkbox*/}
          {/*      size={"small"}*/}
          {/*      checked={hasStatus(CaseStatus.IN_PROGRESS)}*/}
          {/*      onChange={() => statusToggle(CaseStatus.IN_PROGRESS)}*/}
          {/*      name="checkedToday"*/}
          {/*      color="primary"*/}
          {/*    />*/}
          {/*  }*/}
          {/*  label="In Progress"*/}
          {/*/>*/}
          <ToggleFinished
            value={filterParams.finished ? "started" : "notStarted"}
            onChange={(
              event: React.MouseEvent<HTMLElement>,
              updated: string | null,
            ) =>
              setFilterParams((prevState) => ({
                ...prevState,
                finished: updated === "started",
              }))
            }
            exclusive
            aria-label="Assessment Status"
          >
            <ToggleButton value={"started"} aria-label="started">
              Started
            </ToggleButton>
            <ToggleButton value={"notStarted"} aria-label="not started">
              Not Started
            </ToggleButton>
          </ToggleFinished>
        </FilterRowView>
        {errMsg && <ErrorText>{errMsg}</ErrorText>}
      </DialogContent>
      <ActionsDivider />
      <DialogActions>
        <ClinicActionButton
          variant={"outlined"}
          label="RESET"
          lightMode
          onClick={resetFilter}
        />
        <ClinicActionButton label="APPLY" onClick={handleFilter} />
      </DialogActions>
    </ClinicDialog>
  );
};
